import { useQuery, keepPreviousData } from "@tanstack/react-query";
import {
  getTransfersReport,
  type TransferAttempt,
  type TransferBucket,
  type TransferMeta,
} from "../api/transfers";
import type { RangeParams } from "../api/workforce";
import { fmtDateTimeInZone, fmtMs } from "../lib/time";

// R6: attempts, merged, cancelled, no answer, ended with no resolution;
// completion rate; broken out by buyer and by agent. Same date-range controls
// and the same "say how it is computed" discipline as the other Workforce
// tabs. Consult and conference lengths exist only from the day session
// tracking began, and the tab says so rather than showing a blank.

const OUTCOME_LABEL: Record<TransferAttempt["outcome"], string> = {
  merged: "Merged",
  cancelled: "Cancelled",
  no_answer: "No answer",
  unresolved: "No resolution",
  in_progress: "In progress",
};

const OUTCOME_CLASS: Record<TransferAttempt["outcome"], string> = {
  merged: "bg-green-100 text-green-800",
  cancelled: "bg-gray-200 text-gray-600",
  no_answer: "bg-amber-100 text-amber-800",
  unresolved: "bg-red-100 text-red-800",
  in_progress: "bg-blue-100 text-blue-800",
};

function pct(b: TransferBucket): string {
  return b.completion_pct === null ? "—" : `${b.completion_pct.toFixed(1)}%`;
}

function csvEscape(v: unknown): string {
  const s = v === null || v === undefined ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(filename: string, header: string[], rows: unknown[][]) {
  const body = [header, ...rows].map((r) => r.map(csvEscape).join(",")).join("\n");
  const blob = new Blob([body], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function BucketCells({ b }: { b: TransferBucket }) {
  return (
    <>
      <td className="px-4 py-2 text-right font-mono">{b.attempts}</td>
      <td className="px-4 py-2 text-right font-mono text-gray-500">{b.customers}</td>
      <td className="px-4 py-2 text-right font-mono text-green-700">{b.merged}</td>
      <td className="px-4 py-2 text-right font-mono">{b.cancelled}</td>
      <td className="px-4 py-2 text-right font-mono">{b.no_answer}</td>
      <td className={`px-4 py-2 text-right font-mono ${b.unresolved > 0 ? "text-amber-800" : ""}`}>
        {b.unresolved}
      </td>
      <td className="px-4 py-2 text-right font-mono font-semibold">{pct(b)}</td>
      <td className="px-4 py-2 text-right font-mono">{fmtMs(b.median_consult_s)}</td>
      <td className="px-4 py-2 text-right font-mono">{fmtMs(b.median_conference_s)}</td>
    </>
  );
}

function BucketHead({ first }: { first: string }) {
  return (
    <thead className="bg-gray-50 text-left text-gray-500">
      <tr>
        <th className="px-4 py-2">{first}</th>
        <th className="px-4 py-2 text-right">Attempted</th>
        <th className="px-4 py-2 text-right">Customers</th>
        <th className="px-4 py-2 text-right">Merged</th>
        <th className="px-4 py-2 text-right">Cancelled</th>
        <th className="px-4 py-2 text-right">No answer</th>
        <th className="px-4 py-2 text-right">No resolution</th>
        <th className="px-4 py-2 text-right">Completion</th>
        <th className="px-4 py-2 text-right">Consult (median)</th>
        <th className="px-4 py-2 text-right">Conference (median)</th>
      </tr>
    </thead>
  );
}

function TrackingNote({ meta }: { meta: TransferMeta }) {
  if (!meta.session_tracking_since) {
    return (
      <p className="text-sm text-amber-700 mb-3">
        Counts come from the agent activity log. Consult and conference lengths will appear
        for transfers made from now on, once the first one is recorded.
      </p>
    );
  }
  return (
    <p className="text-sm text-gray-500 mb-3">
      Counts come from the agent activity log for every day shown. Consult and conference
      lengths are measured from transfer records, which begin{" "}
      {fmtDateTimeInZone(meta.session_tracking_since, meta.timezone)}; earlier days show none.
    </p>
  );
}

export function WorkforceTransfersTab({ range }: { range: RangeParams }) {
  const q = useQuery({
    queryKey: ["wf-transfers", range],
    queryFn: () => getTransfersReport(range),
    placeholderData: keepPreviousData,
  });

  if (q.isError) return <p className="text-red-600 text-sm">{(q.error as Error).message}</p>;
  if (!q.data) return <p className="text-gray-500 text-sm">Loading…</p>;

  const { meta, total, by_agent, by_destination, by_day, attempts } = q.data;
  const defs = meta.definitions;

  return (
    <div>
      <details className="mb-3 bg-white rounded-lg shadow px-4 py-3 text-sm">
        <summary className="cursor-pointer font-medium text-gray-700">How these figures are computed</summary>
        <dl className="mt-2 grid grid-cols-1 md:grid-cols-[max-content_1fr] gap-x-4 gap-y-1">
          {(["attempts", "customers", "merged", "cancelled", "no_answer", "unresolved",
             "in_progress", "completion_pct", "consult_s", "conference_s"] as const).map((k) => (
            <div key={k} className="contents">
              <dt className="font-medium text-gray-700">{k === "completion_pct" ? "Completion" : k.replace(/_s$/, "").replace(/_/g, " ")}</dt>
              <dd className="text-gray-600">{defs[k] ?? "—"}</dd>
            </div>
          ))}
        </dl>
      </details>
      <TrackingNote meta={meta} />

      <div className="bg-white rounded-lg shadow px-4 py-3 mb-4 flex flex-wrap gap-8 items-center">
        <div className="text-center">
          <div className="text-2xl font-semibold">{total.attempts}</div>
          <div className="text-xs text-gray-500">attempted</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-semibold text-green-700">{total.merged}</div>
          <div className="text-xs text-gray-500">merged</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-semibold">{pct(total)}</div>
          <div className="text-xs text-gray-500">completion</div>
        </div>
        <div className="text-center">
          <div className={`text-2xl font-semibold ${total.unresolved > 0 ? "text-amber-800" : ""}`}>{total.unresolved}</div>
          <div className="text-xs text-gray-500">no resolution</div>
        </div>
        <div className="ml-auto">
          <button
            type="button"
            className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 text-sm"
            disabled={attempts.length === 0}
            onClick={() =>
              downloadCsv(
                `transfers_${meta.date_from}_${meta.date_to}.csv`,
                ["Started", "Agent", "Buyer", "Outcome", "Consult (s)", "Conference (s)", "Ended by", "Customer"],
                attempts.map((a) => [
                  fmtDateTimeInZone(a.started_at, meta.timezone), a.agent_name, a.destination_name,
                  OUTCOME_LABEL[a.outcome], a.consult_s ?? "", a.conference_s ?? "", a.ended_reason ?? "",
                  a.spool_id ?? "",
                ]),
              )
            }
          >
            Download CSV
          </button>
        </div>
      </div>

      <h3 className="text-sm font-medium text-gray-700 mb-1">By buyer</h3>
      <div className="bg-white rounded-lg shadow overflow-x-auto mb-4">
        <table className="w-full text-sm">
          <BucketHead first="Buyer" />
          <tbody className="divide-y divide-gray-100">
            {by_destination.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-6 text-center text-gray-400">No transfers in this range.</td></tr>
            )}
            {by_destination.map((r) => (
              <tr key={r.destination_name}>
                <td className="px-4 py-2 font-medium">{r.destination_name}</td>
                <BucketCells b={r} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 className="text-sm font-medium text-gray-700 mb-1">By agent</h3>
      <div className="bg-white rounded-lg shadow overflow-x-auto mb-4">
        <table className="w-full text-sm">
          <BucketHead first="Agent" />
          <tbody className="divide-y divide-gray-100">
            {by_agent.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-6 text-center text-gray-400">No transfers in this range.</td></tr>
            )}
            {by_agent.map((r) => (
              <tr key={r.agent_user_id ?? "op"}>
                <td className="px-4 py-2 font-medium">{r.agent_name}</td>
                <BucketCells b={r} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {by_day.length > 1 && (
        <>
          <h3 className="text-sm font-medium text-gray-700 mb-1">By day</h3>
          <div className="bg-white rounded-lg shadow overflow-x-auto mb-4">
            <table className="w-full text-sm">
              <BucketHead first="Day" />
              <tbody className="divide-y divide-gray-100">
                {by_day.map((r) => (
                  <tr key={r.day}>
                    <td className="px-4 py-2 font-medium whitespace-nowrap">{r.day}</td>
                    <BucketCells b={r} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <h3 className="text-sm font-medium text-gray-700 mb-1">Every attempt</h3>
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr>
              <th className="px-4 py-2">Started</th>
              <th className="px-4 py-2">Agent</th>
              <th className="px-4 py-2">Buyer</th>
              <th className="px-4 py-2">Outcome</th>
              <th className="px-4 py-2 text-right">Consult</th>
              <th className="px-4 py-2 text-right">Conference</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {attempts.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">No transfers in this range.</td></tr>
            )}
            {attempts.map((a) => (
              <tr key={`${a.agent_user_id}-${a.started_at}`}>
                <td className="px-4 py-2 whitespace-nowrap">{fmtDateTimeInZone(a.started_at, meta.timezone)}</td>
                <td className="px-4 py-2">{a.agent_name}</td>
                <td className="px-4 py-2">{a.destination_name}</td>
                <td className="px-4 py-2">
                  <span className={`px-2 py-0.5 rounded text-xs whitespace-nowrap ${OUTCOME_CLASS[a.outcome]}`}>
                    {OUTCOME_LABEL[a.outcome]}
                  </span>
                  {a.ended_reason === "stale" && (
                    <span className="ml-1 text-[11px] text-gray-400">(end not observed)</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right font-mono">{fmtMs(a.consult_s)}</td>
                <td className="px-4 py-2 text-right font-mono">{fmtMs(a.conference_s)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
